import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { jsPDF } from "jspdf";
import api from "../../../../api/api";
import ToastMessage from "../../../../utils/mui/righttoptoast";
import { useSelector } from "react-redux";
import { DALLAS_BASE } from "../../../../api/bases";
import { getPracticeId } from "../../../../config/practice";

// Collab TSK-4449 (owner decision B): a document staff share from here is ALSO released to the room
// tablet's Medical Records. dshare does that when it gets room_release=1; the tablet shows the shared
// PDF under the name of the file we upload, so the file is named after the document.
export const shareFileName = (procedure) => {
  const raw =
    procedure?.documentdescription ||
    procedure?.note ||
    procedure?.title ||
    procedure?.exam ||
    procedure?.form_name ||
    procedure?.formname ||
    "";
  const clean = String(raw)
    .replace(/[^A-Za-z0-9 ._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80)
    .replace(/[ .]+$/, "");
  return `${clean || "document"}.pdf`;
};

// The chart's patient. A document row that names another patient is never shared (null).
export const sharePatientId = (patid, procedure) => {
  const chart = patid ?? procedure?.patientid;
  if (chart === undefined || chart === null || String(chart).trim() === "") return null;
  const row = procedure?.patientid;
  if (row !== undefined && row !== null && String(row).trim() !== "" && String(row) !== String(chart)) return null;
  return chart;
};

export const buildShareFormData = (blob, procedure) => {
  const formData = new FormData();
  formData.append("attachment", blob, shareFileName(procedure));
  formData.append("room_release", "1");
  return formData;
};

const ShareConfirmModal = ({ open, onClose, procedure, subview,patid }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);

  const practiseid = getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);
const fetchPdfBlob = async (pdfUrl) => {
  const proxiedUrl = withProxy(pdfUrl);
  const res = await fetch(proxiedUrl);
  if (!res.ok) throw new Error("Failed to fetch PDF");
  return await res.blob();
};
useEffect(() => {
  if (open) {
    setToastMessage("");
    setToastSeverity("success");
    setOpenToast(false);
    setIsProcessing(false);
  }
}, [open]);
  const handleToastClose = () => setOpenToast(false);
const PROXY =
  `${DALLAS_BASE}/v1/onboarding-images/proxy?url=`;

const withProxy = (url) =>
  url ? `${PROXY}${encodeURIComponent(url)}` : null;

  if (!open) return null;

  const patientId = procedure?.patientid;

  // ------------------------------------------------------
  // FETCH IMAGING DATA  (no state stored, return directly)
  // ------------------------------------------------------
  const fetchImagingData = async () => {
    try {
      const payload = {
        patientid: patientId,
        imagingresultid:
          subview === 0
            ? procedure.documentclass === "IMAGINGRESULT"
              ? procedure.imagingresultid
              : procedure.encounterdocumentid
            : subview === 1
            ? procedure.imagingresultid
            : procedure.encounterdocumentid,
        category:
          subview === 0
            ? procedure.documentclass === "IMAGINGRESULT"
              ? "Procedures"
              : "ProgressNotes"
            : subview === 1
            ? "Procedures"
            : "ProgressNotes",
      };

      const res = await api.post(
        `/v1/${practiseid}/patient/${patientId}/procedures/image-result`,
        payload
      );

      return res.data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // ------------------------------------------------------
  // FETCH CLINICAL DOCUMENT DATA
  // ------------------------------------------------------
  const fetchClinicalData = async () => {
    try {
      const payload = {
        patient_id: patientId,
        department_id: userConfig?.config?.departmentid,
        clinicaldocument_id: procedure.clinicaldocumentid,
      };

      const res = await api.post(
        `/v1/${practiseid}/clinical-documents-by-id`,
        payload
      );

      return res.data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // ------------------------------------------------------
  // FETCH IMAGE URL FOR EACH PAGE
  // ------------------------------------------------------
  const getImageUrls = async (pages) => {
    const urls = [];

    for (const page of pages) {
      try {
        const res = await api.post(
          `/v1/${practiseid}/procedures/get-image`,
          { url: page.href }
        );

        urls.push(res.data || "");
      } catch (err) {
        console.error(err);
      }
    }

    return urls;
  };

  // ------------------------------------------------------
  // HANDLE SHARE (MAIN LOGIC)
  // ------------------------------------------------------
  const base64ToBlob = (dataUrl) => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

  // one upload for every flow: the chart's patient, the document's name, room_release=1
  const uploadShare = async (pdfBlob, sharePid, okMessage) => {
    const uploadResponse = await api.post(
      `/v1/${practiseid}/pcard/${sharePid}/dshare`,
      buildShareFormData(pdfBlob, procedure),
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    if (uploadResponse.status === 200 || uploadResponse.status === 201) {
      const onTablet = uploadResponse?.data?.roomRelease === "released";
      setToastMessage(onTablet ? `${okMessage} (also on the room tablet)` : okMessage);
      setToastSeverity("success");
      setOpenToast(true);
      setTimeout(() => {
        onClose();
      }, 1200);
    } else {
      throw new Error("Upload failed");
    }
  };
//   const handleShare = async () => {
//     setIsProcessing(true);

//     try {
//       // 1️⃣ Fetch correct document
//       let documentData = [];

//       if (
//         subview === 3 ||
//         (subview === 0 && procedure?.documentclass === "CLINICALDOCUMENT")
//       ) {
//         documentData = await fetchClinicalData();
//       } else {
//         documentData = await fetchImagingData();
//       }

//       const pages = documentData[0]?.pages || [];
//       if (!pages.length) throw new Error("No pages found");

//       // 2️⃣ Fetch all images
//       const imageUrls = await getImageUrls(pages);

//       // 3️⃣ Build PDF
//       const pdf = new jsPDF({ unit: "px", format: "a4" });

//       for (let i = 0; i < imageUrls.length; i++) {
//         const img = new Image();

//         await new Promise((resolve) => {
//           img.onload = resolve;
//           img.src = imageUrls[i];
//         });

//         const width = pdf.internal.pageSize.getWidth();
//         const height = (img.height * width) / img.width;

//         if (i > 0) pdf.addPage();
//         pdf.addImage(img, "PNG", 0, 0, width, height);
//       }

//       // 4️⃣ Create blob + preview
//       const pdfBlob = pdf.output("blob");
//       const pdfURL = URL.createObjectURL(pdfBlob);

//       // 🔥 Open PDF Preview in New Tab
//     //   window.open(pdfURL, "_blank");
//   const formData = new FormData();
//       formData.append("attachment", pdfBlob, "document.pdf");
    
// const uploadResponse = await api.post(
//         `/v1/${practiseid}/pcard/${patientId}/dshare`,
//         formData,
//         { headers: { "Content-Type": "multipart/form-data" } }
//       );

//       if (uploadResponse.status === 200 || uploadResponse.status === 201) {
//         setToastMessage("Document shared successfully");
//         setToastSeverity("success");
//         setOpenToast(true);
//           setTimeout(() => {
//     onClose();
//   }, 1200);

//       } else {
//         throw new Error("Upload failed");
//       }
//     } catch (err) {
//       console.error(err);
//       setToastMessage("Failed to generate preview");
//       setToastSeverity("error");
//       setOpenToast(true);
//     } finally {
//       setIsProcessing(false);
//     }
//   };
const handleShare = async () => {
  const sharePid = sharePatientId(patid, procedure);
  if (sharePid === null) {
    setToastMessage("This document does not belong to this patient's chart, so it was not shared");
    setToastSeverity("error");
    setOpenToast(true);
    return;
  }
  setIsProcessing(true);

  try {
// ===========================
// 🔥 FORMS SHARE FLOW
// ===========================
if (procedure?.documentclass === "Form") {
  try {
    const pdfUrl = `https://api.brainymed.com/chimlw/pdf/questionaireforms/${procedure.id}_${procedure.form_id}.pdf`;

    const pdfBlob = await fetchPdfBlob(pdfUrl);

    await uploadShare(pdfBlob, sharePid, "Form shared successfully");

    return; // ⛔ stop normal flow
  } catch (err) {
    console.error(err);
    setToastMessage("Failed to share form");
    setToastSeverity("error");
    setOpenToast(true);
    setIsProcessing(false);
    return;
  }
}

    // 🔥 EARLY EXIT FOR OPENEMR
    if (procedure?.source === "openemr") {
      if (!procedure?.imgbasefile) {
        throw new Error("No OpenEMR document found");
      }

      const pdfBlob = base64ToBlob(procedure.imgbasefile);
      await uploadShare(pdfBlob, sharePid, "Document shared successfully");

      return; // ⛔ stop further execution
    }

    // ===========================
    // NORMAL FLOW (NON-OPENEMR)
    // ===========================

    let documentData = [];

    if (
      subview === 3 ||
      (subview === 0 && procedure?.documentclass === "CLINICALDOCUMENT")
    ) {
      documentData = await fetchClinicalData();
    } else {
      documentData = await fetchImagingData();
    }

    const pages = documentData[0]?.pages || [];
    if (!pages.length) throw new Error("No pages found");

    const imageUrls = await getImageUrls(pages);

    const pdf = new jsPDF({ unit: "px", format: "a4" });

    for (let i = 0; i < imageUrls.length; i++) {
      const img = new Image();

      await new Promise((resolve) => {
        img.onload = resolve;
        img.src = imageUrls[i];
      });

      const width = pdf.internal.pageSize.getWidth();
      const height = (img.height * width) / img.width;

      if (i > 0) pdf.addPage();
      pdf.addImage(img, "PNG", 0, 0, width, height);
    }

    const pdfBlob = pdf.output("blob");
    await uploadShare(pdfBlob, sharePid, "Document shared successfully");
  } catch (err) {
    console.error(err);
    setToastMessage("Failed to generate preview");
    setToastSeverity("error");
    setOpenToast(true);
  } finally {
    setIsProcessing(false);
  }
};

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/50"></div>

      <div className="relative bg-white p-6 rounded-xl shadow-lg w-[350px] z-50">
        <h2 className="text-lg font-semibold text-[#1d2951]">Share Document</h2>

        <p className="mt-2 text-gray-700">
          Do you want to share this document with the patient?
        </p>

        {isProcessing ? (
          <div className="flex flex-col items-center mt-4">
            <CircularProgress size={28} />
            <p className="text-sm text-gray-600 mt-2">Processing...</p>
          </div>
        ) : (
          <div className="flex justify-end gap-3 mt-6">
            <button
              className="px-4 py-1.5 rounded-lg border text-gray-600"
              onClick={onClose}
            >
              No
            </button>

            <button
              className="px-4 py-1.5 rounded-lg bg-[#5C8496] text-white"
              onClick={handleShare}
            >
              Yes
            </button>
          </div>
        )}
      </div>

      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />
    </div>
  );
};

export default ShareConfirmModal;
